import React, {
  ChangeEvent,
  useCallback,
  KeyboardEventHandler,
  useMemo,
  useEffect,
  useState,
} from 'react';

import { Tooltip, IconButton } from '@wix/wix-ui-tpa/cssVars';
import { Attachment } from '@wix/wix-ui-icons-common/on-stage';
import { useStyles } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { useAppSelector } from '@/store';
import {
  selectOneChatComposed,
  selectOneChatSettings,
  oneChatSettingsModel,
} from '@/features';
import { validateFile } from '@/utils';

import settingsParams from 'AiAssistantWidget/settingsParams';
import stylesParams from 'AiAssistantWidget/stylesParams';
import {
  useRootProps,
  useWidgetSettings,
} from 'AiAssistantWidget/Widget/contexts';

import { SendIcon } from '../svg';

import { st, classes, cssStates } from './InputOneChat.st.css';

type InputProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  onUploadFile?: (args: { file?: File; error?: Error }) => void;
};

const getCharsRule = (
  text: string,
  font: string,
  maxWidth: number,
): { chars: number; addDots: boolean } => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return { chars: text.length, addDots: false };
  }

  context.font = font;

  let width = 0;
  let charCount = 0;

  for (const char of text) {
    width += context.measureText(char).width;
    if (width > maxWidth) {
      break;
    }
    charCount++;
  }

  return text.length === charCount
    ? { chars: charCount, addDots: false }
    : { chars: charCount - 3, addDots: true };
};

export const InputOneChat = ({
  value,
  onChange,
  onSubmit,
  disabled,
  onUploadFile,
}: InputProps) => {
  const { t } = useTranslation();
  const { isEditor, isMobile } = useEnvironment();
  const { direction } = useRootProps();
  const settings = useWidgetSettings();
  const styles = useStyles();

  const current = useAppSelector(selectOneChatSettings.currentStatus);
  const isAwaitingAnswer = useAppSelector(
    (state) => state.messages.awaitingAnswer,
  );

  const shouldDisableInput = useAppSelector(
    selectOneChatComposed.shouldDisableInput,
  );

  const shouldDisableInputWhenManual = useAppSelector(
    selectOneChatComposed.shouldDisableInputWhenManual,
  );

  const isManual = useAppSelector(selectOneChatSettings.isManual);
  const allowFileUploadInOfflineMode = useAppSelector(
    selectOneChatSettings.allowFileUploadInOfflineMode,
  );
  const isFileUploadEnabled = isManual || allowFileUploadInOfflineMode;

  const [Textarea, setTextarea] =
    React.useState<React.ComponentType<any> | null>(null);

  const submitRef = React.useRef<any>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');

  const [isUsingIME, setIsUsingIME] = useState(false);

  const inputPlaceholder = useMemo(() => {
    if (isAwaitingAnswer) {
      return t('app.widget.input.disabled.placeholder');
    }

    switch (current) {
      case oneChatSettingsModel.CurrentStatus.AI_ASSISTANT_ONLINE:
        return settings.get(settingsParams.textInputPlaceholder);

      case oneChatSettingsModel.CurrentStatus.MANUAL_ONLINE:
        if (shouldDisableInputWhenManual) {
          return t('app.widget.input.messagesAfterContactForm');
        }

        return settings.get(settingsParams.manualChatTextInputPlaceholder);

      case oneChatSettingsModel.CurrentStatus.OFFLINE:
        return t('app.settings.text.disabledInputTooltip');
    }
  }, [settings, t, current, shouldDisableInputWhenManual, isAwaitingAnswer]);

  const disabledInput = useMemo(
    () => disabled || (shouldDisableInput && !isEditor),
    [isEditor, disabled, shouldDisableInput],
  );

  const disabledInputStyles = useMemo(
    () => disabledInput || isAwaitingAnswer,
    [disabledInput, isAwaitingAnswer],
  );

  const disabledSubmit = useMemo(
    () => !value.length || disabledInputStyles,
    [value.length, disabledInputStyles],
  );

  const disabledFileUpload = useMemo(
    () => disabledInputStyles || !isFileUploadEnabled,
    [disabledInputStyles, isFileUploadEnabled],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !isUsingIME) {
        e.preventDefault();
        if (!disabledSubmit) {
          onSubmit();
        }
      }
    },
    [onSubmit, isUsingIME, disabledSubmit],
  );

  const handleFile = useCallback(
    async (file: File) => {
      const { allowed, reason } = validateFile(file);

      if (allowed) {
        onUploadFile?.({ file });
      } else {
        onUploadFile?.({ error: new Error(reason) });
      }
    },
    [onUploadFile],
  );

  const onFileIconClick = useCallback(() => {
    if (disabledFileUpload) {
      return;
    }
    fileInputRef.current?.click();
  }, [disabledFileUpload]);

  const onFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabledFileUpload) {
        return;
      }

      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }

      e.target.value = '';
    },
    [disabledFileUpload, handleFile],
  );

  const onPaste: React.ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      if (disabledFileUpload) {
        return;
      }

      const files = event.clipboardData.files;

      if (files && files.length > 0) {
        const file = files[0];
        handleFile(file);
        event.preventDefault();
      }
    },
    [disabledFileUpload, handleFile],
  );

  useEffect(() => {
    if (!Textarea) {
      // TODO: add skeleton loader
      import('react-textarea-autosize').then((module) => {
        setTextarea(() => module.default);
      });
    }
  }, [Textarea]);

  useEffect(() => {
    const el = submitRef.current?.wrappedComponentRef?.innerComponentRef;
    if (Textarea && el) {
      el.disabled = disabledSubmit;
      if (!disabledInput) {
        textareaRef.current?.focus();
      }
    }
  }, [Textarea, disabledInput, disabledSubmit]);

  useEffect(() => {
    if (!Textarea) {
      return;
    }

    /**
    Since Chrome & Edge no longer allow styling inside ::placeholder due to browser optimizations (like applying text-overflow: ellipsis)
    we need to calculate the number of characters that fit in the input content area to imitate ellipsis for placeholder
     */
    const fontParam = styles.get(stylesParams.inputTextFont);
    const fontFamilies = fontParam.family?.split(',');
    const font = `${fontParam.size}px ${fontFamilies?.[0] ?? 'sans-serif'}`;
    const inputHorizontalPadding = 70;
    const containerHorizontalPadding = 48;
    const contentWidth =
      styles.get(stylesParams.widgetWidth) -
      inputHorizontalPadding -
      containerHorizontalPadding;
    const { chars, addDots } = getCharsRule(
      inputPlaceholder,
      font,
      contentWidth,
    );

    setDisplayedPlaceholder(
      `${inputPlaceholder.slice(0, chars)}${addDots ? '...' : ''}`,
    );
  }, [Textarea, inputPlaceholder, styles]);

  const sendButton = (
    <IconButton
      aria-disabled={disabledSubmit}
      disabled={disabledSubmit}
      innerRef={submitRef}
      icon={
        <SendIcon
          className={st(
            classes.submitIcon,
            cssStates({ emptyInput: !value.length }),
          )}
        />
      }
    />
  );

  const trigger = (
    <div
      className={st(classes.submitTooltipTrigger)}
      onClick={!isAwaitingAnswer && !disabledSubmit ? onSubmit : undefined}
    >
      {sendButton}
    </div>
  );

  if (!Textarea) {
    return null;
  }

  return (
    <div
      className={st(
        classes.root,
        cssStates({ disabled: disabledInputStyles, isMobile }),
      )}
      data-awaiting-answer={isAwaitingAnswer ? 'true' : undefined}
    >
      <div className={st(classes.inputContainer)} dir={direction ?? 'ltr'}>
        <Textarea
          value={value}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          maxRows={6}
          className={st(classes.input)}
          placeholder={displayedPlaceholder}
          disabled={disabledInput}
          autoFocus={!isEditor}
          ref={textareaRef}
          onCompositionStart={() => setIsUsingIME(true)}
          onCompositionEnd={() => setIsUsingIME(false)}
          aria-label={t('app.widget.ariallabel.messageInput')}
        />
        {isFileUploadEnabled && (
          <>
            <input
              ref={fileInputRef}
              tabIndex={-1}
              accept="image/*,.heic,.doc,.docx,.ppt,.pptx,.xlsx,.xls,.pdf"
              type="file"
              style={{ display: 'none' }}
              onChange={onFileInputChange}
            />

            <div className={st(classes.attach)}>
              <IconButton
                className={st(classes.attachIcon)}
                aria-disabled={disabledFileUpload}
                disabled={disabledFileUpload}
                onClick={onFileIconClick}
                icon={<Attachment />}
              />
            </div>
          </>
        )}
        <div className={st(classes.submit)}>
          {isAwaitingAnswer ? (
            <Tooltip
              content={t('app.widget.input.disabled.sendIcon.tooltip')}
              appendTo="scrollParent"
            >
              {trigger}
            </Tooltip>
          ) : (
            trigger
          )}
        </div>
      </div>
    </div>
  );
};
